import type { Category } from '@wix/add-panel-common';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryNavigationState, Path, PathsState } from '../../AddPanel.types';
import type { CategoryViewRef } from '../../CategoryView/CategoryView.types';
import { findSectionInGroups, getCategoryFirstVisibleSection, isPathToSectionExist, pathsEqual } from '../../utils';

export interface UsePanelNavigationProps {
  deepLinkPath: Path | null;
  pathsState: PathsState | null;
  categories: Category[];
  scrollActorRef: RefObject<CategoryViewRef>;
}

export interface UsePanelNavigationResult {
  path: Path;
  deepLinkPath: Path | null;
  setPath: (newPath: Path) => void;
  shouldUseDeepLinkPath: boolean;
  pathsStateController: {
    getPathsState: () => PathsState;
    getCategory: (categoryId: string) => CategoryNavigationState | undefined;
    setLastPath: (lastPath: Path) => void;
    updateCategory: (categoryId: string, sectionId: string, top: number) => void;
  };
  scrollPreventionController: {
    shouldPrevent: () => boolean;
    preventNextAutoScroll: () => void;
    allowAutoScroll: () => void;
  };
}

const DEFAULT_PATHS_STATE: PathsState = {
  positions: {},
};

const getLastPath = (pathsState: PathsState | null, categories: Category[]): Path | null => {
  if (!pathsState?.lastPath) {
    return null;
  }
  const [categoryId, sectionId] = pathsState.lastPath;
  if (!categoryId || !sectionId) {
    return null;
  }
  const lastCategory = categories.find(({ systemId }) => systemId === categoryId);
  if (!lastCategory) {
    return null;
  }
  if (findSectionInGroups(sectionId, lastCategory.groups)) {
    return [categoryId, sectionId];
  }
  return [categoryId, getCategoryFirstVisibleSection(lastCategory)?.systemId];
};

export const usePanelNavigation = ({
  scrollActorRef,
  deepLinkPath: deepLinkPathOuter,
  pathsState,
  categories,
}: UsePanelNavigationProps): UsePanelNavigationResult => {
  const [deepLinkPath, setDeepLinkPath] = useState(deepLinkPathOuter);
  const isDeepLinkPathValid = Boolean(deepLinkPath && isPathToSectionExist(deepLinkPath, categories));
  const [firstCategory] = categories;
  const pathsStateRef = useRef<PathsState>({ ...DEFAULT_PATHS_STATE, ...pathsState });
  const lastScrolledDeepLinkPathRef = useRef<Path | null>(null);

  const shouldUseDeepLinkPath = Boolean(isDeepLinkPathValid && deepLinkPath !== lastScrolledDeepLinkPathRef.current);

  const [path, setPath] = useState<Path>(
    deepLinkPath && isDeepLinkPathValid
      ? deepLinkPath
      : getLastPath(pathsState, categories) || [
          firstCategory.systemId,
          getCategoryFirstVisibleSection(firstCategory)?.systemId,
        ],
  );

  const scrollPreventionController = useMemo<UsePanelNavigationResult['scrollPreventionController']>(() => {
    let shouldPrevent = false;

    return {
      preventNextAutoScroll: () => {
        shouldPrevent = true;
      },
      shouldPrevent: () => shouldPrevent,
      allowAutoScroll: () => {
        shouldPrevent = false;
      },
    };
  }, []);

  const setPathsStateLastPath = useCallback((lastPath: Path) => {
    pathsStateRef.current = {
      ...pathsStateRef.current,
      lastPath,
    };
  }, []);

  const getPathsStateCategoryState = useCallback(
    (categoryId: string): CategoryNavigationState | undefined => pathsStateRef.current.positions?.[categoryId],
    [],
  );

  const updatePathsStateCategory = useCallback((categoryId: string, sectionId: string, top: number) => {
    pathsStateRef.current = {
      ...pathsStateRef.current,
      positions: {
        ...pathsStateRef.current?.positions,
        [categoryId]: {
          section: sectionId,
          top,
        },
      },
    };
  }, []);

  const pathsStateController = useMemo(
    () => ({
      getPathsState: () => pathsStateRef.current,
      getCategory: getPathsStateCategoryState,
      setLastPath: setPathsStateLastPath,
      updateCategory: updatePathsStateCategory,
    }),
    [setPathsStateLastPath, getPathsStateCategoryState, updatePathsStateCategory],
  );

  useEffect(() => {
    /*
     * this useEffect is used to scroll when path changed
     */

    if (lastScrolledDeepLinkPathRef.current !== deepLinkPath) {
      return;
    }

    if (scrollPreventionController.shouldPrevent()) {
      scrollPreventionController.allowAutoScroll();
      return;
    }

    const [categoryId] = path;
    const top = pathsStateRef.current.positions?.[categoryId]?.top || 0;

    scrollActorRef.current?.scrollTo({ top });
  }, [scrollActorRef, deepLinkPath, path, isDeepLinkPathValid, scrollPreventionController]);

  useEffect(() => {
    /*
     * this useEffect is used to scroll when deepLinkPath changed
     */
    const deepLinkPathChanged = lastScrolledDeepLinkPathRef.current !== deepLinkPath;

    if (!deepLinkPath || !isDeepLinkPathValid || !deepLinkPathChanged) {
      return;
    }

    lastScrolledDeepLinkPathRef.current = deepLinkPath;
    setPath(deepLinkPath);
    const [categoryId, sectionId] = deepLinkPath;

    if (!sectionId) {
      return;
    }

    scrollActorRef.current?.scrollToSectionTop(sectionId, (top) => {
      pathsStateController.updateCategory(categoryId, sectionId, top);
    });
  }, [scrollActorRef, deepLinkPath, isDeepLinkPathValid, pathsStateController]);

  useEffect(() => {
    pathsStateController.setLastPath(path);
  }, [path, pathsStateController]);

  useEffect(() => {
    const shouldUpdatePath = deepLinkPathOuter && (!deepLinkPath || !pathsEqual(deepLinkPathOuter, deepLinkPath));

    if (shouldUpdatePath) {
      setDeepLinkPath(deepLinkPathOuter);
    }
  }, [deepLinkPathOuter, deepLinkPath, scrollActorRef]);

  return {
    path,
    deepLinkPath,
    setPath,
    shouldUseDeepLinkPath,
    pathsStateController,
    scrollPreventionController,
  };
};
